import React, { useState, useEffect } from 'react';
import { CompositeView, SimpleView, GridView, FilterWidget, Widget, Button, notification } from '@digital/ui';
import { Table, Select, Switch, Modal, Input, Tooltip, Icon } from 'antd';
import { SearchButton } from '@app/components/FormButtons';
import { hasPermissionPerfil } from '@app/helpers/permissions';
import { EGuidPerfilAcesso } from '@app/enums/EGuidPerfilAcesso';
import {
  getControleSuspensao,
  desbloquearPagamento,
  exportarControleSuspensao,
  ControleSuspensaoItem,
  ControleSuspensaoResult,
} from '../../api/controleSuspensao';
import { PaginationResult } from '../../api/pendenciaDocumental';

const STATUS_OPCOES = [
  { value: 'ativos', label: 'Bloqueados' },
  { value: 'desbloqueados', label: 'Desbloqueados' },
  { value: 'todos', label: 'Todos' },
];

const ControleSuspensao = () => {
  const [resultado, setResultado] = useState<ControleSuspensaoResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [pageAtual, setPageAtual] = useState(1);
  const [sizeAtual, setSizeAtual] = useState(20);

  const [filtroAdmContrato, setFiltroAdmContrato] = useState<string | undefined>(undefined);
  const [filtroContrato, setFiltroContrato] = useState<number | undefined>(undefined);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');

  const [modalDesbloqueio, setModalDesbloqueio] = useState<ControleSuspensaoItem | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [modalHistorico, setModalHistorico] = useState<ControleSuspensaoItem | null>(null);

  const ehAdministradorContrato = hasPermissionPerfil([EGuidPerfilAcesso.AdministradorContratoMRN]);

  const buscar = (
    admContrato: string | undefined,
    numeroContrato: number | undefined,
    status: string,
    page: number,
    size: number
  ) => {
    setLoading(true);
    getControleSuspensao({
      admContratoId: admContrato,
      numeroContrato: numeroContrato,
      statusBloqueio: status,
      page: page,
      pageSize: size,
    })
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        if (data) setResultado(data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    buscar(undefined, undefined, 'todos', 1, 20);
  }, []);

  const handleFiltrar = () => {
    setPageAtual(1);
    buscar(filtroAdmContrato, filtroContrato, filtroStatus, 1, sizeAtual);
  };

  const handleLimpar = () => {
    setFiltroAdmContrato(undefined);
    setFiltroContrato(undefined);
    setFiltroStatus('todos');
    setPageAtual(1);
    buscar(undefined, undefined, 'todos', 1, sizeAtual);
  };

  const handleExportar = () => {
    setExportando(true);
    exportarControleSuspensao({
      admContratoId: filtroAdmContrato,
      numeroContrato: filtroContrato,
      statusBloqueio: filtroStatus,
    }).finally(() => setExportando(false));
  };

  const handleTableChange = (p: any) => {
    setPageAtual(p.current);
    setSizeAtual(p.pageSize);
    buscar(filtroAdmContrato, filtroContrato, filtroStatus, p.current, p.pageSize);
  };

  const abrirModalDesbloqueio = (item: ControleSuspensaoItem) => {
    if (!ehAdministradorContrato) return;
    setModalDesbloqueio(item);
    setJustificativa('');
  };

  const confirmarDesbloqueio = () => {
    if (!modalDesbloqueio) return;
    if (!justificativa || justificativa.trim().length < 10) {
      notification.error('Justificativa deve ter no mínimo 10 caracteres.');
      return;
    }
    setSalvando(true);
    desbloquearPagamento(modalDesbloqueio.id, justificativa.trim())
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        const notifs = data && data.notifications;
        if (notifs && notifs.length > 0) {
          notification.error(notifs.map((n: any) => n.message).join(' '));
        } else {
          notification.success('Pagamento desbloqueado com sucesso.');
          setModalDesbloqueio(null);
          buscar(filtroAdmContrato, filtroContrato, filtroStatus, pageAtual, sizeAtual);
        }
      })
      .catch(() => notification.error('Erro ao desbloquear pagamento.'))
      .finally(() => setSalvando(false));
  };

  const cancelarDesbloqueio = () => {
    setModalDesbloqueio(null);
    setJustificativa('');
  };

  const paginacao: PaginationResult<ControleSuspensaoItem> | null = resultado ? resultado.bloqueios : null;
  const items = paginacao ? paginacao.items : [];
  const administradoresContrato = resultado ? resultado.administradoresContrato : [];
  const numerosContrato = resultado ? resultado.numerosContrato : [];

  const columns = [
    {
      title: 'Nº Contrato',
      dataIndex: 'numeroContrato',
      key: 'numeroContrato',
      width: 110,
      render: (v: number) => (v != null ? v : '-'),
    },
    {
      title: 'Adm. do Contrato',
      dataIndex: 'administradorContrato',
      key: 'administradorContrato',
      width: 200,
      render: (v: string) => v || '-',
    },
    {
      title: 'Destinatário',
      dataIndex: 'destinatario',
      key: 'destinatario',
      width: 200,
      render: (v: string) => v || '-',
    },
    {
      title: 'Rastreio',
      dataIndex: 'envioCodigoRastreio',
      key: 'envioCodigoRastreio',
      width: 140,
      render: (v: string) => v || '-',
    },
    {
      title: 'Data Envio',
      dataIndex: 'envioData',
      key: 'envioData',
      width: 120,
      render: (v: string) => (v ? new Date(v).toLocaleDateString('pt-BR') : '-'),
    },
    {
      title: 'Prazo',
      dataIndex: 'envioPrazoData',
      key: 'envioPrazoData',
      width: 120,
      render: (v: string) => (v ? new Date(v).toLocaleDateString('pt-BR') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'bloqueioAtivo',
      key: 'bloqueioAtivo',
      width: 130,
      render: (ativo: boolean, row: ControleSuspensaoItem) => {
        if (!ativo) {
          return (
            <Tooltip title="Ver justificativa do desbloqueio">
              <span
                style={{ cursor: 'pointer', color: '#888' }}
                onClick={() => setModalHistorico(row)}
              >
                Desbloqueado <Icon type="message" style={{ marginLeft: 4 }} />
              </span>
            </Tooltip>
          );
        }
        if (!ehAdministradorContrato) {
          return <span style={{ color: '#cf1322' }}>Bloqueado</span>;
        }
        return (
          <Tooltip title="Desbloquear pagamento">
            <Switch
              checked={true}
              checkedChildren="Bloqueado"
              unCheckedChildren="Ativo"
              onChange={() => abrirModalDesbloqueio(row)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <CompositeView>
      <SimpleView>
        <GridView onSubmit={handleFiltrar}>
          <FilterWidget
            active={[]}
            actions={[
              <SearchButton type="primary" key="buscar" onClick={handleFiltrar} />,
              <Button key="limpar" onClick={handleLimpar} style={{ marginLeft: 8 }}>Limpar</Button>,
              <Button
                key="exportar"
                onClick={handleExportar}
                loading={exportando}
                style={{ marginLeft: 8 }}
                icon="download"
              >
                Exportar
              </Button>,
            ]}
            title="Filtros">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 0' }}>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Adm. de Contrato</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todos"
                  style={{ width: 240 }}
                  value={filtroAdmContrato}
                  onChange={(v: any) => setFiltroAdmContrato(v || undefined)}>
                  {administradoresContrato.map(n => (
                    <Select.Option key={n} value={n}>{n}</Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Nº Contrato</div>
                <Input
                  allowClear
                  placeholder="Todos"
                  style={{ width: 140 }}
                  value={filtroContrato}
                  onChange={(e: any) => {
                    const v = e.target.value;
                    setFiltroContrato(v === '' ? undefined : Number(v));
                  }}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Status do Bloqueio</div>
                <Select
                  placeholder="Todos"
                  style={{ width: 180 }}
                  value={filtroStatus}
                  onChange={(v: any) => setFiltroStatus(v || 'todos')}>
                  {STATUS_OPCOES.map(o => (
                    <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </FilterWidget>
          <Widget
            title={'Controle de Suspensão' + (paginacao ? ' — ' + paginacao.totalItems + ' registros' : '')}
          >
            <Table
              className="no-bold-table"
              dataSource={items}
              columns={columns}
              rowKey="id"
              size="small"
              loading={loading}
              scroll={{ x: 1020 }}
              onChange={handleTableChange}
              pagination={{
                current: pageAtual,
                pageSize: sizeAtual,
                total: paginacao ? paginacao.totalItems : 0,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
              }}
              locale={{ emptyText: 'Nenhum bloqueio encontrado.' }}
            />
          </Widget>
        </GridView>
      </SimpleView>

      <Modal
        title="Desbloquear Pagamento"
        visible={!!modalDesbloqueio}
        onOk={confirmarDesbloqueio}
        onCancel={cancelarDesbloqueio}
        confirmLoading={salvando}
        okText="Confirmar Desbloqueio"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !justificativa || justificativa.trim().length < 10 }}
        cancelButtonProps={{ style: { background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } }}
      >
        <p style={{ marginBottom: 8 }}>
          Contrato: <strong>{modalDesbloqueio ? modalDesbloqueio.numeroContrato : ''}</strong>
        </p>
        <p style={{ marginBottom: 8 }}>Informe a justificativa do desbloqueio (mínimo 10 caracteres):</p>
        <Input.TextArea
          rows={4}
          value={justificativa}
          onChange={(e: any) => setJustificativa(e.target.value)}
          maxLength={1000}
          placeholder="Descreva o motivo do desbloqueio..."
        />
      </Modal>

      <Modal
        title="Justificativa do Desbloqueio"
        visible={!!modalHistorico}
        onCancel={() => setModalHistorico(null)}
        footer={null}
      >
        {modalHistorico && (
          <div>
            <p style={{ marginBottom: 8 }}>
              Contrato: <strong>{modalHistorico.numeroContrato}</strong>
            </p>
            <p style={{ marginBottom: 8 }}>
              Data do desbloqueio:{' '}
              {modalHistorico.dataDesbloqueio
                ? new Date(modalHistorico.dataDesbloqueio).toLocaleString('pt-BR')
                : '-'}
            </p>
            <p style={{ marginBottom: 8 }}>
              Responsável: {modalHistorico.usuarioDesbloqueio || '-'}
            </p>
            <p style={{ marginBottom: 4 }}>Justificativa:</p>
            <p style={{ whiteSpace: 'pre-wrap' }}>{modalHistorico.justificativaDesbloqueio || '-'}</p>
          </div>
        )}
      </Modal>
    </CompositeView>
  );
};

export default ControleSuspensao;
